import React, { useState, useEffect } from "react";
import { Divider, Menu, MenuItem } from "@mui/material";
import TextInput from "../../Shared/TextInput";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../Shared/Select";
import { makeStyles } from "@material-ui/core/styles";
import { Pencil, PowerOff, Trash2, Save, EllipsisVertical, X, Plus, Hash, Timer, Rocket, FlaskConical, List, ArrowLeft, Unlink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { dataService } from "../../../Services/data/data.service";
import { flowBoxDeviceStatus, flowBoxButtonAction, externalApiTypes } from "../../../Config/constants";
import Button from "../../Shared/Buttons/Button";
import IconButton from "../../Shared/Buttons/IconButton";
import Label from "../../Shared/Label";
import Chip from "../../Shared/ui/Chip";
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from "../../Shared/Dialog";
import FlowBoxSetupLogView from "./FlowBoxSetupLogView";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_INFO = {
  [flowBoxDeviceStatus.PENDING_CONFIGURATION]: { tKey: "flowBox.status.pendingConfiguration", variant: "warning" },
  [flowBoxDeviceStatus.REGISTERED]:         { tKey: "flowBox.status.registered",        variant: "warning" },
  [flowBoxDeviceStatus.ACTIVE]:             { tKey: "flowBox.status.active",             variant: "success" },
  [flowBoxDeviceStatus.DEREGISTERED]:       { tKey: "flowBox.status.deregistered",       variant: "danger"  },
  [flowBoxDeviceStatus.INACTIVE]:           { tKey: "flowBox.status.inactive",           variant: "default" },
  [flowBoxDeviceStatus.AVAILABLE_TO_CONNECT]: { tKey: "flowBox.status.availableToConnect", variant: "info"  },
  [flowBoxDeviceStatus.SETUP]:              { tKey: "flowBox.status.setup",              variant: "info"    },
};

const formatDate = (val) => {
  if (!val) return "-";
  const d = new Date(val);
  if (isNaN(d)) return "-";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

// ─── Fixed-duration helpers ─────────────────────────────────────────────────────

const DURATION_UNIT = { SECONDS: 1, MINUTES: 60, HOURS: 3600 };
const DEFAULT_DURATION = { value: 5, unit: DURATION_UNIT.MINUTES }; // 5 minutes

// Split a stored second count into the largest whole unit for a friendly edit experience.
const decomposeDuration = (totalSeconds) => {
  if (!totalSeconds || totalSeconds <= 0) return { ...DEFAULT_DURATION };
  if (totalSeconds % DURATION_UNIT.HOURS === 0) return { value: totalSeconds / DURATION_UNIT.HOURS, unit: DURATION_UNIT.HOURS };
  if (totalSeconds % DURATION_UNIT.MINUTES === 0) return { value: totalSeconds / DURATION_UNIT.MINUTES, unit: DURATION_UNIT.MINUTES };
  return { value: totalSeconds, unit: DURATION_UNIT.SECONDS };
};

const formatDuration = (totalSeconds) => {
  if (!totalSeconds || totalSeconds <= 0) return "-";
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h ? `${h}h` : null, m ? `${m}m` : null, s ? `${s}s` : null].filter(Boolean).join(" ");
};

function DurationInput({ valueNum, unit, onValueChange, onUnitChange }) {
  const { t } = useTranslation();
  const UNIT_OPTS = [
    { value: DURATION_UNIT.SECONDS, label: t("flowBox.endpoint.unitSeconds") },
    { value: DURATION_UNIT.MINUTES, label: t("flowBox.endpoint.unitMinutes") },
    { value: DURATION_UNIT.HOURS,   label: t("flowBox.endpoint.unitHours") },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: "#374151", fontFamily: "Lato, sans-serif" }}>{t("flowBox.endpoint.durationLabel")}</span>
      <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
        <TextInput
          type="number"
          value={valueNum}
          onChange={(e) => onValueChange(Math.max(1, parseInt(e.target.value, 10) || 1))}
          inputProps={{ min: 1 }}
          style={{ flex: 1, minWidth: 0 }}
        />
        <div style={{ width: 130, flexShrink: 0 }}>
          <Select value={unit} onChange={onUnitChange} fullwidth>
            <SelectTrigger>
              {UNIT_OPTS.find((o) => o.value === unit)?.label}
            </SelectTrigger>
            <SelectContent>
              {UNIT_OPTS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

// ─── ConfirmDialog ────────────────────────────────────────────────────────────

function ConfirmDialog({ open, title, message, confirmLabel, onConfirm, onCancel, destructive = false }) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs">
      <DialogHeader closeable={false}>
        <DialogTitle>{title}</DialogTitle>
      </DialogHeader>
      <DialogBody>
        <p style={{ margin: 0, fontSize: 14, fontFamily: "Lato, sans-serif", color: "#6b7280" }}>{message}</p>
      </DialogBody>
      <DialogFooter>
        <Button variant="light" onClick={onCancel}>{t("common.cancel")}</Button>
        <Button variant={destructive ? "destructive" : "primary"} onClick={onConfirm}>
          {confirmLabel || t("common.confirm")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ─── PhysicalButton ───────────────────────────────────────────────────────────

const BUTTON_DOME_COLORS = {
  1: { highlight: "#6ab2ff", mid: "#1258c4", shadow: "#082458" },  // Blue
  2: { highlight: "#f5f268", mid: "#b0bc00", shadow: "#6c7200" },  // Yellow
  3: { highlight: "#f56a6a", mid: "#c81010", shadow: "#760808" },  // Red
  4: { highlight: "#4de86a", mid: "#0ca030", shadow: "#065518" },  // Green
  5: { highlight: "#ffffff", mid: "#dcdcdc", shadow: "#a8a8a8" },  // White
};

const usePhysicalButtonStyles = makeStyles({
  bezel: {
    width: 60,
    height: 60,
    borderRadius: "50%",
    background: "radial-gradient(circle at 40% 35%, #585858, #1a1a1a 70%)",
    boxShadow: "0 3px 7px rgba(0,0,0,0.35), 0 1px 2px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -1px 3px rgba(0,0,0,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    flexShrink: 0,
    userSelect: "none",
  },
  dome: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    boxShadow: "inset 0 2px 4px rgba(255,255,255,0.22), inset 0 -3px 6px rgba(0,0,0,0.28), 0 1px 3px rgba(0,0,0,0.35)",
  },
  led: {
    position: "absolute",
    top: 3,
    right: 3,
    width: 9,
    height: 9,
    borderRadius: "50%",
    background: "#22c55e",
    boxShadow: "0 0 5px rgba(34,197,94,0.85)",
    border: "1px solid rgba(0,0,0,0.25)",
  },
});

function PhysicalButton({ index, isMapped }) {
  const classes = usePhysicalButtonStyles();
  const colors = BUTTON_DOME_COLORS[index] || BUTTON_DOME_COLORS[1];
  return (
    <div className={classes.bezel}>
      <div
        className={classes.dome}
        style={{ background: `radial-gradient(circle at 36% 30%, ${colors.highlight} 0%, ${colors.mid} 48%, ${colors.shadow} 100%)` }}
      />
      {isMapped && <div className={classes.led} />}
    </div>
  );
}

// ─── AssignActionDialog ───────────────────────────────────────────────────────

function AssignActionDialog({ open, onClose, buttonIndex, externalApis, deviceId, userId, companyId, initialApiId, initialActionId, initialCountPerPress, initialFixedDurationSeconds, onSaved }) {
  const { t } = useTranslation();

  const initialDuration = decomposeDuration(initialFixedDurationSeconds);

  // Navigation: null = choice screen, "select" = pick existing, "create" = new endpoint form
  const [intentMode, setIntentMode] = useState(initialApiId ? "select" : null);

  // Select-mode state
  const [selectedApiId, setSelectedApiId] = useState(initialApiId ?? "");
  const [apiActionId, setApiActionId] = useState(initialActionId ?? flowBoxButtonAction.MEASURE);
  const [counterAction, setCounterAction] = useState(
    initialActionId === flowBoxButtonAction.SUBTRACT ? flowBoxButtonAction.SUBTRACT : flowBoxButtonAction.MEASURE
  );
  const [countPerPress, setCountPerPress] = useState(initialCountPerPress ?? 1);
  const [durationValue, setDurationValue] = useState(initialDuration.value);
  const [durationUnit, setDurationUnit] = useState(initialDuration.unit);
  const [saving, setSaving] = useState(false);

  // Create-mode state
  const [triggerName, setTriggerName] = useState("");
  const [triggerTypeId, setTriggerTypeId] = useState(null);
  const [newTriggerAction, setNewTriggerAction] = useState(flowBoxButtonAction.TOGGLE);
  const [createCounterAction, setCreateCounterAction] = useState(flowBoxButtonAction.MEASURE);
  const [createCountPerPress, setCreateCountPerPress] = useState(1);
  const [createDurationValue, setCreateDurationValue] = useState(DEFAULT_DURATION.value);
  const [createDurationUnit, setCreateDurationUnit] = useState(DEFAULT_DURATION.unit);
  const [createSaving, setCreateSaving] = useState(false);

  const ACTION_OPTS_STOPWATCH = [
    { value: flowBoxButtonAction.TOGGLE,       label: t("flowBox.action.startStop") },
    { value: flowBoxButtonAction.START,        label: t("flowBox.action.start") },
    { value: flowBoxButtonAction.STOP,         label: t("flowBox.action.stop") },
    { value: flowBoxButtonAction.LOG_DURATION, label: t("flowBox.action.logDuration") },
  ];

  const ACTION_OPTS_COUNTER = [
    { value: flowBoxButtonAction.MEASURE,  label: t("flowBox.action.add") },
    { value: flowBoxButtonAction.SUBTRACT, label: t("flowBox.action.subtract") },
  ];

  useEffect(() => {
    if (open) {
      const d = decomposeDuration(initialFixedDurationSeconds);
      setIntentMode(initialApiId ? "select" : null);
      setSelectedApiId(initialApiId ?? "");
      setApiActionId(initialActionId ?? flowBoxButtonAction.MEASURE);
      setCounterAction(
        initialActionId === flowBoxButtonAction.SUBTRACT ? flowBoxButtonAction.SUBTRACT : flowBoxButtonAction.MEASURE
      );
      setCountPerPress(initialCountPerPress ?? 1);
      setDurationValue(d.value);
      setDurationUnit(d.unit);
      setTriggerName("");
      setTriggerTypeId(null);
      setNewTriggerAction(flowBoxButtonAction.TOGGLE);
      setCreateCounterAction(flowBoxButtonAction.MEASURE);
      setCreateCountPerPress(1);
      setCreateDurationValue(DEFAULT_DURATION.value);
      setCreateDurationUnit(DEFAULT_DURATION.unit);
    }
  }, [open, initialApiId, initialActionId, initialCountPerPress, initialFixedDurationSeconds]);

  const handleGoBack = () => {
    setIntentMode(null);
    setTriggerName("");
    setTriggerTypeId(null);
    setNewTriggerAction(flowBoxButtonAction.TOGGLE);
    setCreateCounterAction(flowBoxButtonAction.MEASURE);
    setCreateCountPerPress(1);
    setCreateDurationValue(DEFAULT_DURATION.value);
    setCreateDurationUnit(DEFAULT_DURATION.unit);
  };

  // Select-mode helpers
  const selectedApi = externalApis.find((a) => a.id === selectedApiId);
  const isStopwatch = selectedApi?.externalApiTypeId === externalApiTypes.STOPWATCH;
  const isCounter = selectedApi && !isStopwatch;

  const handleTriggerChange = (val) => {
    setSelectedApiId(val);
    setApiActionId(flowBoxButtonAction.MEASURE);
    setCounterAction(flowBoxButtonAction.MEASURE);
    setCountPerPress(1);
  };

  const isDurationSelected = isStopwatch && apiActionId === flowBoxButtonAction.LOG_DURATION;

  const handleAssign = () => {
    if (!selectedApiId) return;
    setSaving(true);
    dataService.setButtonMapping({
      deviceId,
      buttonIndex,
      externalApiId: selectedApiId,
      apiActionId: isStopwatch ? apiActionId : counterAction,
      countPerPress: isCounter ? Math.max(1, parseInt(countPerPress, 10) || 1) : 1,
      fixedDurationSeconds: isDurationSelected ? Math.max(1, durationValue) * durationUnit : null,
      updatedByUserId: userId,
    })
      .then(() => { toast.success(t("flowBox.endpoint.buttonAssigned", { buttonIndex })); onSaved(); })
      .catch(() => toast.error(t("flowBox.endpoint.assignFailed")))
      .finally(() => setSaving(false));
  };

  // Create-mode helpers
  const isCreateStopwatch = triggerTypeId === externalApiTypes.STOPWATCH;
  const isCreateDuration = isCreateStopwatch && newTriggerAction === flowBoxButtonAction.LOG_DURATION;
  const typeSelected = triggerTypeId !== null;
  const canCreate = triggerName.trim() && typeSelected && !createSaving;

  const handleTypeSelect = (type) => {
    setTriggerTypeId(type);
    setNewTriggerAction(flowBoxButtonAction.TOGGLE);
    setCreateCounterAction(flowBoxButtonAction.MEASURE);
    setCreateCountPerPress(1);
    setCreateDurationValue(DEFAULT_DURATION.value);
    setCreateDurationUnit(DEFAULT_DURATION.unit);
  };

  const handleCreate = () => {
    if (!canCreate) return;
    setCreateSaving(true);
    dataService.createTriggerForButton({
      deviceId,
      buttonIndex,
      externalApiTypeId: triggerTypeId,
      name: triggerName.trim(),
      companyId,
      insertedByUserId: userId,
      apiActionId: isCreateStopwatch ? newTriggerAction : createCounterAction,
      countPerPress: isCreateStopwatch ? 1 : Math.max(1, parseInt(createCountPerPress, 10) || 1),
      fixedDurationSeconds: isCreateDuration ? Math.max(1, createDurationValue) * createDurationUnit : null,
    })
      .then(() => { toast.success(t("flowBox.endpoint.endpointCreated", { buttonIndex })); onSaved(); })
      .catch(() => toast.error(t("flowBox.endpoint.endpointCreateFailed")))
      .finally(() => setCreateSaving(false));
  };

  const typeCardStyle = (type) => {
    const isActive = triggerTypeId === type;
    return {
      flex: 1,
      height: typeSelected ? 44 : 80,
      borderRadius: 8,
      border: `1px solid ${isActive ? "#2563eb" : "#e5e7eb"}`,
      background: isActive ? "#eff6ff" : "#f9fafb",
      color: isActive ? "#1d4ed8" : "#374151",
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      cursor: "pointer",
      transition: "height 0.2s ease, background 0.15s ease, border-color 0.15s ease, color 0.15s ease",
      outline: "none",
      userSelect: "none",
      boxSizing: "border-box",
    };
  };

  const choiceCardStyle = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: "28px 16px",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    background: "#f9fafb",
    cursor: "pointer",
    userSelect: "none",
    outline: "none",
    transition: "border-color 0.15s ease, background 0.15s ease",
  };

  const dialogTitle = intentMode === "create"
    ? t("flowBox.endpoint.newEndpointTitle", { buttonIndex })
    : t("flowBox.endpoint.assignActionTitle", { buttonIndex });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm">
      <DialogHeader closeable={false}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {intentMode !== null && !initialApiId && (
              <IconButton size="small" ariaLabel={t("common.back")} onClick={handleGoBack}>
                <ArrowLeft />
              </IconButton>
            )}
            <DialogTitle>{dialogTitle}</DialogTitle>
          </div>
          <IconButton size="small" ariaLabel={t("flowBox.configure.closeDialog")} onClick={onClose}><X /></IconButton>
        </div>
      </DialogHeader>

      <DialogBody>
        {intentMode === null && (
          <div style={{ display: "flex", gap: 12 }}>
            <div
              role="button"
              tabIndex={0}
              style={choiceCardStyle}
              onClick={() => setIntentMode("select")}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setIntentMode("select"); }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#2563eb"; e.currentTarget.style.background = "#eff6ff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.background = "#f9fafb"; }}
            >
              <List size={28} color="#6b7280" />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "Lato, sans-serif", marginBottom: 4 }}>
                  {t("flowBox.endpoint.useExistingEndpoint")}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "Lato, sans-serif", lineHeight: 1.4 }}>
                  {t("flowBox.endpoint.useExistingEndpointDesc")}
                </div>
              </div>
            </div>
            <div
              role="button"
              tabIndex={0}
              style={choiceCardStyle}
              onClick={() => setIntentMode("create")}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setIntentMode("create"); }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#2563eb"; e.currentTarget.style.background = "#eff6ff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.background = "#f9fafb"; }}
            >
              <Plus size={28} color="#6b7280" />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "Lato, sans-serif", marginBottom: 4 }}>
                  {t("flowBox.endpoint.createNewEndpoint")}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "Lato, sans-serif", lineHeight: 1.4 }}>
                  {t("flowBox.endpoint.createNewEndpointDesc")}
                </div>
              </div>
            </div>
          </div>
        )}

        {intentMode === "select" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#374151", fontFamily: "Lato, sans-serif" }}>{t("flowBox.endpoint.endpointLabel")}</span>
              <Select
                value={selectedApiId}
                onChange={handleTriggerChange}
                fullwidth
                searchable
                placeholder={t("flowBox.endpoint.selectEndpointPlaceholder")}
              >
                <SelectTrigger>
                  {externalApis.find((a) => a.id === selectedApiId)?.name}
                </SelectTrigger>
                <SelectContent>
                  {externalApis.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isStopwatch && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#374151", fontFamily: "Lato, sans-serif" }}>{t("flowBox.endpoint.buttonActionLabel")}</span>
                <Select value={apiActionId} onChange={setApiActionId} fullwidth>
                  <SelectTrigger>
                    {ACTION_OPTS_STOPWATCH.find((o) => o.value === apiActionId)?.label}
                  </SelectTrigger>
                  <SelectContent>
                    {ACTION_OPTS_STOPWATCH.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {isDurationSelected && (
              <DurationInput
                valueNum={durationValue}
                unit={durationUnit}
                onValueChange={setDurationValue}
                onUnitChange={setDurationUnit}
              />
            )}
            {isCounter && (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#374151", fontFamily: "Lato, sans-serif" }}>{t("flowBox.endpoint.directionLabel")}</span>
                  <Select value={counterAction} onChange={setCounterAction} fullwidth>
                    <SelectTrigger>
                      {ACTION_OPTS_COUNTER.find((o) => o.value === counterAction)?.label}
                    </SelectTrigger>
                    <SelectContent>
                      {ACTION_OPTS_COUNTER.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <TextInput
                  fullWidth
                  type="number"
                  label={t("flowBox.endpoint.countPerPress")}
                  value={countPerPress}
                  onChange={(e) => setCountPerPress(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  inputProps={{ min: 1 }}
                />
              </>
            )}
          </div>
        )}

        {intentMode === "create" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <p style={{ margin: 0, fontSize: 13, color: "#6b7280", fontFamily: "Lato, sans-serif", lineHeight: 1.5 }}>
              {t("flowBox.endpoint.newEndpointDesc")}
            </p>
            <TextInput
              fullWidth
              label={t("flowBox.endpoint.endpointNameLabel")}
              value={triggerName}
              onChange={(e) => setTriggerName(e.target.value)}
              placeholder={t("flowBox.endpoint.endpointNamePlaceholder")}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#374151", fontFamily: "Lato, sans-serif" }}>{t("flowBox.endpoint.typeLabel")}</span>
              <div style={{ display: "flex", gap: 10 }}>
                <div
                  role="button"
                  tabIndex={0}
                  style={typeCardStyle(externalApiTypes.NUMERIC)}
                  onClick={() => handleTypeSelect(externalApiTypes.NUMERIC)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleTypeSelect(externalApiTypes.NUMERIC); }}
                >
                  <Hash size={typeSelected ? 15 : 22} style={{ transition: "width 0.2s ease, height 0.2s ease" }} />
                  <span style={{ fontSize: typeSelected ? 12 : 14, fontWeight: 600, fontFamily: "Lato, sans-serif", transition: "font-size 0.2s ease" }}>
                    {t("flowBox.endpoint.typeCounter")}
                  </span>
                </div>
                <div
                  role="button"
                  tabIndex={0}
                  style={typeCardStyle(externalApiTypes.STOPWATCH)}
                  onClick={() => handleTypeSelect(externalApiTypes.STOPWATCH)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleTypeSelect(externalApiTypes.STOPWATCH); }}
                >
                  <Timer size={typeSelected ? 15 : 22} style={{ transition: "width 0.2s ease, height 0.2s ease" }} />
                  <span style={{ fontSize: typeSelected ? 12 : 14, fontWeight: 600, fontFamily: "Lato, sans-serif", transition: "font-size 0.2s ease" }}>
                    {t("flowBox.endpoint.typeStopwatch")}
                  </span>
                </div>
              </div>
            </div>
            {typeSelected && isCreateStopwatch && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#374151", fontFamily: "Lato, sans-serif" }}>{t("flowBox.endpoint.buttonActionLabel")}</span>
                <Select value={newTriggerAction} onChange={setNewTriggerAction} fullwidth>
                  <SelectTrigger>
                    {ACTION_OPTS_STOPWATCH.find((o) => o.value === newTriggerAction)?.label}
                  </SelectTrigger>
                  <SelectContent>
                    {ACTION_OPTS_STOPWATCH.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {typeSelected && isCreateDuration && (
              <DurationInput
                valueNum={createDurationValue}
                unit={createDurationUnit}
                onValueChange={setCreateDurationValue}
                onUnitChange={setCreateDurationUnit}
              />
            )}
            {typeSelected && !isCreateStopwatch && (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#374151", fontFamily: "Lato, sans-serif" }}>{t("flowBox.endpoint.directionLabel")}</span>
                  <Select value={createCounterAction} onChange={setCreateCounterAction} fullwidth>
                    <SelectTrigger>
                      {ACTION_OPTS_COUNTER.find((o) => o.value === createCounterAction)?.label}
                    </SelectTrigger>
                    <SelectContent>
                      {ACTION_OPTS_COUNTER.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <TextInput
                  fullWidth
                  type="number"
                  label={t("flowBox.endpoint.countPerPress")}
                  value={createCountPerPress}
                  onChange={(e) => setCreateCountPerPress(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  inputProps={{ min: 1 }}
                />
              </>
            )}
          </div>
        )}
      </DialogBody>

      <DialogFooter>
        <Button variant="light" onClick={onClose}>{t("common.cancel")}</Button>
        {intentMode === "select" && (
          <Button onClick={handleAssign} disabled={!selectedApiId || saving}>
            {saving ? t("flowBox.endpoint.assigning") : t("flowBox.endpoint.assign")}
          </Button>
        )}
        {intentMode === "create" && (
          <Button startIcon={<Save size={14} />} onClick={handleCreate} disabled={!canCreate}>
            {createSaving ? t("flowBox.endpoint.creating") : t("flowBox.endpoint.createAndAssign")}
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
}

// ─── ButtonMappingRow ─────────────────────────────────────────────────────────

function ButtonMappingRow({ index, mapping, externalApis, companyId, userId, deviceId, onSaved }) {
  const { t } = useTranslation();
  const [assignOpen, setAssignOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  const ACTION_OPTS_STOPWATCH = [
    { value: flowBoxButtonAction.TOGGLE,       label: t("flowBox.action.startStop") },
    { value: flowBoxButtonAction.START,        label: t("flowBox.action.start") },
    { value: flowBoxButtonAction.STOP,         label: t("flowBox.action.stop") },
    { value: flowBoxButtonAction.LOG_DURATION, label: t("flowBox.action.logDuration") },
  ];

  const assignedApi = mapping ? externalApis.find((a) => a.id === mapping.externalApiId) : null;
  const isStopwatch = assignedApi?.externalApiTypeId === externalApiTypes.STOPWATCH;
  const actionLabel = isStopwatch
    ? mapping?.apiActionId === flowBoxButtonAction.LOG_DURATION
      ? `${t("flowBox.action.logDuration")} - ${formatDuration(mapping?.fixedDurationSeconds)}`
      : ACTION_OPTS_STOPWATCH.find((o) => o.value === mapping?.apiActionId)?.label
    : mapping
      ? `${mapping.apiActionId === flowBoxButtonAction.SUBTRACT ? "-" : "+"}${mapping.countPerPress ?? 1} per press`
      : null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "12px 0" }}>
      <PhysicalButton index={index} isMapped={!!mapping} />

      {!mapping ? (
        <Button variant="light" startIcon={<Plus size={14} />} onClick={() => setAssignOpen(true)}>
          {t("flowBox.configure.addAction")}
        </Button>
      ) : (
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ flex: 1, fontSize: 14, fontFamily: "Lato, sans-serif", color: "#111827" }}>
            {assignedApi?.name || t("flowBox.configure.unknownEndpoint")}
            {actionLabel && (
              <span style={{ marginLeft: 6, fontSize: 12, color: "#6b7280" }}>({actionLabel})</span>
            )}
          </span>
          <IconButton size="small" ariaLabel={t("flowBox.configure.changeAction")} onClick={() => setAssignOpen(true)}>
            <Pencil />
          </IconButton>
          <IconButton size="small" variant="destructive" ariaLabel={t("flowBox.configure.removeAction")} onClick={() => setConfirmClear(true)}>
            <Trash2 />
          </IconButton>
        </div>
      )}

      <ConfirmDialog
        open={confirmClear}
        title={t("flowBox.configure.removeActionTitle", { buttonIndex: index })}
        message={t("flowBox.configure.removeActionMessage", { name: assignedApi?.name || t("flowBox.configure.unknownEndpoint") })}
        confirmLabel={t("flowBox.configure.removeAction")}
        destructive
        onConfirm={() => {
          setConfirmClear(false);
          dataService.clearButtonMapping({ deviceId, buttonIndex: index, updatedByUserId: userId })
            .then(() => { toast.success(t("flowBox.configure.actionRemoved", { buttonIndex: index })); onSaved(); })
            .catch(() => toast.error(t("flowBox.configure.actionRemoveFailed")));
        }}
        onCancel={() => setConfirmClear(false)}
      />

      <AssignActionDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        buttonIndex={index}
        externalApis={externalApis}
        deviceId={deviceId}
        userId={userId}
        companyId={companyId}
        initialApiId={mapping?.externalApiId ?? ""}
        initialActionId={mapping?.apiActionId ?? flowBoxButtonAction.MEASURE}
        initialCountPerPress={mapping?.countPerPress ?? 1}
        initialFixedDurationSeconds={mapping?.fixedDurationSeconds ?? null}
        onSaved={() => { setAssignOpen(false); onSaved(); }}
      />
    </div>
  );
}

// ─── FlowBoxDeviceConfigureDialog ─────────────────────────────────────────────

function FlowBoxDeviceConfigureDialog({ open, onClose, deviceId, userId, onSaved }) {
  const { t } = useTranslation();
  const classes = useStyles();

  const [device, setDevice] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [externalApis, setExternalApis] = useState([]);
  const [companyCount, setCompanyCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [confirmDeregister, setConfirmDeregister] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [confirmUnassign, setConfirmUnassign] = useState(false);
  const [activating, setActivating] = useState(false);
  const [enteringSetup, setEnteringSetup] = useState(false);

  const loadMappings = () =>
    Promise.all([
      dataService.getButtonMappingsByDeviceId(deviceId),
      device ? dataService.getExternalApisByCompanyId(device.companyId) : Promise.resolve(null),
    ]).then(([maps, apis]) => {
      setMappings(maps || []);
      if (apis) setExternalApis(apis);
    });

  const load = () => {
    if (!deviceId) return;
    setLoading(true);
    dataService.getFlowBoxDeviceById(deviceId)
      .then((dev) => {
        setDevice(dev);
        setRenameValue(dev?.displayName || "");
        return Promise.all([
          dataService.getButtonMappingsByDeviceId(deviceId),
          dataService.getExternalApisByCompanyId(dev.companyId),
          dataService.getAllCompaniesByUserId(userId),
        ]);
      })
      .then(([maps, apis, companies]) => {
        setMappings(maps || []);
        setExternalApis(apis || []);
        setCompanyCount((companies || []).length);
      })
      .catch(() => toast.error(t("flowBox.configure.loadFailed")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open && deviceId) load();
    if (!open) {
      setDevice(null); setMappings([]); setExternalApis([]); setCompanyCount(0); setIsRenaming(false);
    }
  }, [open, deviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRename = () => {
    if (!renameValue.trim()) return;
    setRenameSaving(true);
    dataService.renameFlowBoxDevice({ deviceId, displayName: renameValue.trim(), updatedByUserId: userId })
      .then(() => {
        toast.success(t("flowBox.configure.deviceRenamed"));
        setDevice((d) => ({ ...d, displayName: renameValue.trim() }));
        setIsRenaming(false);
        onSaved();
      })
      .catch(() => toast.error(t("flowBox.configure.deviceRenameFailed")))
      .finally(() => setRenameSaving(false));
  };

  const handleUnassign = () => {
    setConfirmUnassign(false);
    dataService.unassignFlowBoxDevice({ deviceId, updatedByUserId: userId })
      .then(() => { toast.success(t("flowBox.configure.deviceUnassigned")); onSaved(); load(); })
      .catch(() => toast.error(t("flowBox.configure.deviceUnassignFailed")));
  };

  const handleDeregister = () => {
    setConfirmDeregister(false);
    dataService.deregisterFlowBoxDevice({ deviceId, updatedByUserId: userId })
      .then(() => { toast.success(t("flowBox.configure.deviceRemoved")); onSaved(); onClose(); })
      .catch(() => toast.error(t("flowBox.configure.deviceRemoveFailed")));
  };

  const handleDeactivate = () => {
    setConfirmDeactivate(false);
    dataService.deactivateFlowBoxDevice({ deviceId, updatedByUserId: userId })
      .then(() => { toast.success(t("flowBox.configure.deviceDeactivated")); onSaved(); load(); })
      .catch(() => toast.error(t("flowBox.configure.deviceDeactivateFailed")));
  };

  const handleActivate = () => {
    setActivating(true);
    dataService.activateFlowBoxDevice({ deviceId, updatedByUserId: userId })
      .then(() => { toast.success(t("flowBox.setup.deviceActivated")); onSaved(); load(); })
      .catch(() => toast.error(t("flowBox.setup.deviceActivateFailed")))
      .finally(() => setActivating(false));
  };

  const handleEnterSetup = () => {
    setEnteringSetup(true);
    dataService.enterFlowBoxSetupMode({ deviceId, updatedByUserId: userId })
      .then(() => { toast.success(t("flowBox.setup.enteredSetup")); onSaved(); load(); })
      .catch(() => toast.error(t("flowBox.setup.enterSetupFailed")))
      .finally(() => setEnteringSetup(false));
  };

  const isSetup = device?.statusId === flowBoxDeviceStatus.SETUP;
  const isActive = device?.statusId === flowBoxDeviceStatus.ACTIVE;
  const statusInfo = device ? (STATUS_INFO[device.statusId] || { tKey: "flowBox.status.unknown", variant: "default" }) : null;
  const buttonCount = device?.buttonCount || 5;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth={false} style={{ maxWidth: 1200, width: "100%" }}>
        <DialogHeader closeable={false}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <DialogTitle>
              {device ? (device.displayName || device.imei || t("flowBox.configure.title")) : t("flowBox.configure.title")}
            </DialogTitle>
            <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
              <IconButton
                size="small"
                ariaLabel={t("flowBox.configure.deviceActions")}
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                disabled={!device}
              >
                <EllipsisVertical />
              </IconButton>
              <IconButton size="small" ariaLabel={t("flowBox.configure.closeDialog")} onClick={onClose}>
                <X />
              </IconButton>
            </div>
          </div>
        </DialogHeader>

        <DialogBody>
          {loading && (
            <div style={{ padding: "48px", textAlign: "center", color: "#9ca3af", fontFamily: "Lato, sans-serif" }}>
              {t("common.loading")}
            </div>
          )}

          {!loading && device && (
            <div className={classes.layout}>
              {/* Sidebar: device info + setup log */}
              <div className={classes.sidebar}>
                <div className={classes.card}>
                  <Label>{t("flowBox.configure.deviceInfo")}</Label>
                  <div className={classes.infoGrid}>
                    <span className={classes.infoLabel}>{t("flowBox.configure.fieldName")}</span>
                    {isRenaming ? (
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <TextInput
                          size="small"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          inputProps={{ maxLength: 100 }}
                          style={{ flex: 1, minWidth: 0 }}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleRename();
                            if (e.key === "Escape") setIsRenaming(false);
                          }}
                        />
                        <IconButton
                          size="extraSmall"
                          ariaLabel={t("flowBox.configure.saveName")}
                          onClick={handleRename}
                          disabled={renameSaving || !renameValue.trim()}
                        >
                          <Save />
                        </IconButton>
                      </span>
                    ) : (
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <span>{device.displayName || "-"}</span>
                        <IconButton size="extraSmall" ariaLabel={t("flowBox.configure.renameDevice")} onClick={() => setIsRenaming(true)}>
                          <Pencil />
                        </IconButton>
                      </span>
                    )}
                    <span className={classes.infoLabel}>{t("flowBox.configure.fieldStatus")}</span>
                    <Chip variant={statusInfo.variant} size="small" style={{ justifySelf: "start" }}>{t(statusInfo.tKey)}</Chip>
                    <span className={classes.infoLabel}>{t("flowBox.configure.fieldImei")}</span>
                    {device.imei
                      ? <span className={classes.imei}>{device.imei}</span>
                      : <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: 14, fontFamily: "Lato, sans-serif" }}>{t("flowBox.configure.noImei")}</span>
                    }
                    <span className={classes.infoLabel}>{t("flowBox.configure.fieldType")}</span>
                    <span>{device.deviceTypeName || device.deviceTypeCode || "-"}</span>
                    <span className={classes.infoLabel}>{t("flowBox.configure.fieldCommissioned")}</span>
                    <span>{formatDate(device.commissionedOn)}</span>
                    <span className={classes.infoLabel}>{t("flowBox.configure.fieldLastSeen")}</span>
                    <span>{formatDate(device.lastSeenOn)}</span>
                    {companyCount > 1 && (
                      <>
                        <span className={classes.infoLabel}>{t("flowBox.configure.fieldCompany")}</span>
                        <span>{device.companyName || "-"}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Setup log (only in Setup mode) */}
                {isSetup && (
                  <div className={classes.card}>
                    <FlowBoxSetupLogView deviceId={deviceId} />
                  </div>
                )}
              </div>

              {/* Main: button mappings */}
              <div className={classes.main}>
                <div className={classes.card}>
                  <Label>{t("flowBox.configure.buttonMappings")}</Label>
                  <p className={classes.hint}>
                    {t("flowBox.configure.buttonMappingsHint")}
                  </p>
                  <div style={{ marginTop: 12 }}>
                    {Array.from({ length: buttonCount }, (_, i) => i + 1).map((btnIdx) => (
                      <React.Fragment key={btnIdx}>
                        {btnIdx > 1 && <Divider />}
                        <ButtonMappingRow
                          index={btnIdx}
                          mapping={mappings.find((m) => m.buttonIndex === btnIdx) || null}
                          externalApis={externalApis}
                          companyId={device.companyId}
                          userId={userId}
                          deviceId={deviceId}
                          onSaved={loadMappings}
                        />
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                {/* Setup actions */}
                {isSetup && (
                  <div className={classes.card} style={{ marginTop: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                      <div>
                        <Label>{t("flowBox.setup.readyTitle")}</Label>
                        <p className={classes.hint} style={{ marginTop: 2 }}>
                          {t("flowBox.setup.readyHint")}
                        </p>
                      </div>
                      <Button
                        startIcon={<Rocket size={14} />}
                        onClick={handleActivate}
                        disabled={activating}
                      >
                        {activating ? t("flowBox.setup.activating") : t("flowBox.setup.activate")}
                      </Button>
                    </div>
                  </div>
                )}

              </div>
            </div>
          )}
        </DialogBody>
      </Dialog>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {isActive && (
          <MenuItem
            onClick={() => { setMenuAnchor(null); handleEnterSetup(); }}
            disabled={enteringSetup}
          >
            <FlaskConical size={14} style={{ marginRight: 8 }} />
            {t("flowBox.setup.enterSetup")}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => { setMenuAnchor(null); setConfirmUnassign(true); }}
          disabled={
            device?.statusId === flowBoxDeviceStatus.PENDING_CONFIGURATION ||
            device?.statusId === flowBoxDeviceStatus.DEREGISTERED
          }
        >
          <Unlink size={14} style={{ marginRight: 8 }} />
          {t("flowBox.configure.unassignDevice")}
        </MenuItem>
        <MenuItem
          onClick={() => { setMenuAnchor(null); setConfirmDeactivate(true); }}
          disabled={
            device?.statusId === flowBoxDeviceStatus.INACTIVE ||
            device?.statusId === flowBoxDeviceStatus.DEREGISTERED
          }
        >
          <PowerOff size={14} style={{ marginRight: 8 }} />
          {t("flowBox.configure.deactivate")}
        </MenuItem>
        <MenuItem
          onClick={() => { setMenuAnchor(null); setConfirmDeregister(true); }}
          disabled={device?.statusId === flowBoxDeviceStatus.DEREGISTERED}
          style={{ color: "#ef4444" }}
        >
          <Trash2 size={14} style={{ marginRight: 8 }} />
          {t("flowBox.configure.remove")}
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={confirmUnassign}
        title={t("flowBox.configure.unassignDeviceTitle")}
        message={t("flowBox.configure.unassignDeviceMessage", { name: device?.displayName || device?.imei })}
        confirmLabel={t("flowBox.configure.unassignDevice")}
        onConfirm={handleUnassign}
        onCancel={() => setConfirmUnassign(false)}
      />
      <ConfirmDialog
        open={confirmDeregister}
        title={t("flowBox.configure.removeDeviceTitle")}
        message={t("flowBox.configure.removeDeviceMessage", { name: device?.displayName || device?.imei })}
        confirmLabel={t("flowBox.configure.remove")}
        destructive
        onConfirm={handleDeregister}
        onCancel={() => setConfirmDeregister(false)}
      />
      <ConfirmDialog
        open={confirmDeactivate}
        title={t("flowBox.configure.deactivateDeviceTitle")}
        message={t("flowBox.configure.deactivateDeviceMessage", { name: device?.displayName || device?.imei })}
        confirmLabel={t("flowBox.configure.deactivate")}
        onConfirm={handleDeactivate}
        onCancel={() => setConfirmDeactivate(false)}
      />
    </>
  );
}

export default FlowBoxDeviceConfigureDialog;

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  layout: {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",
  },
  sidebar: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    width: "100%",
    [theme.breakpoints.up("md")]: { width: "360px", flexShrink: 0 },
  },
  main: { flex: 1, minWidth: 0 },
  card: {
    backgroundColor: "#F9F9F9",
    borderRadius: "8px",
    padding: "16px",
    border: "1px solid #EDEDED",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    gap: "8px 16px",
    alignItems: "center",
    marginTop: "8px",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
  },
  infoLabel: { color: "#6b7280", fontWeight: "500" },
  imei: { fontFamily: "monospace", fontSize: "14px" },
  hint: { fontSize: "13px", color: "#6b7280", margin: "4px 0 0", fontFamily: "Lato, sans-serif" },
}));
